// ═══════════════════════════════════════════════════════════════════════════
// ECONOMIA DELLA COMMESSA — FONTE UNICA DEI CALCOLI (audit economico, ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Ogni schermata che mostra costi, ricavi o margini (Controllo Economico, Report Commessa,
// Cabina di Regia, Insight, Clienti, Centro Operativo, Il tuo anno, Priorità, relazioni…)
// deve usare QUESTE funzioni. Prima ognuna aveva la sua formula e i margini non
// coincidevano tra una schermata e l'altra. Regole:
//   • importi letti sempre con numIt(): capisce "1.250,00", "1250,50", "12,5" e i numeri
//     (parseFloat su "1.250,00" dava 1,25 e su "12,50" dava 12);
//   • i movimenti con tipo "ricavo" (es. interventi di manutenzione a tariffa) sono RICAVI,
//     mai costi;
//   • i movimenti nati dai cartellini (presenzeRef) contano come manodopera SOLO se la fonte
//     ore dell'organizzazione è "presenze" (altrimenti, cambiando fonte, si conterebbero due
//     volte: cartellini + programmazione/rapportini);
//   • ordini di acquisto: imponibile IVA esclusa (imponibileOda);
//   • ricavi della commessa = importo commessa + ricavi registrati come movimenti.
// ═══════════════════════════════════════════════════════════════════════════
import { parseValutaItaliana } from "./clienteHelpers";

/** Numero da un importo/quantità scritto in qualunque forma (italiana o numerica). */
export function numIt(v) {
  const n = parseValutaItaliana(v);
  return Number.isFinite(n) ? n : 0;
}

export const eRicavo = (m) => m?.tipo === "ricavo";

/** Il movimento conta come costo della commessa? */
export function contaComeCosto(m, fontOre) {
  if (!m || eRicavo(m) || m.eliminato) return false;
  if (m.presenzeRef && fontOre && fontOre !== "presenze") return false;
  return true;
}

/** Somma dei costi registrati come movimenti. */
export function costiDaMovimenti(movimenti, fontOre) {
  return (movimenti || []).reduce((s, m) => s + (contaComeCosto(m, fontOre) ? numIt(m.importo) : 0), 0);
}

/** Somma dei ricavi registrati come movimenti (es. interventi di manutenzione). */
export function ricaviDaMovimenti(movimenti) {
  return (movimenti || []).reduce((s, m) => s + (eRicavo(m) && !m.eliminato ? numIt(m.importo) : 0), 0);
}

/**
 * Costi calcolati dal Controllo Economico e salvati sul budget (costiCalcolati):
 * manodopera da Programmazione/Rapportini (con fonte Presenze è già nei movimenti) e
 * noleggi mezzi d'opera in corso. Così tutte le viste sommano gli stessi costi.
 */
export function costiCalcolatiDaBudget(budget) {
  const c = budget?.costiCalcolati;
  if (!c) return 0;
  const manodopera = c.fonteOre && c.fonteOre !== "presenze" ? numIt(c.manodopera) : 0;
  // ore del diario «Le mie ore» (W403): contano con qualunque fonte delle ore
  return manodopera + numIt(c.diario) + numIt(c.noleggi);
}

/** Importo commessa dal documento budget. */
export const importoCommessa = (budget) => numIt(budget?.importoCommessa);

/**
 * Varianti e lavori extra ACCETTATI dal cliente (da eseguire + da fatturare + fatturati), salvati sul budget
 * (variantiCalcolate) dalla scheda Varianti. L'importo contrattuale non cambia mai:
 * Budget + Varianti = Totale commessa.
 */
export const variantiDaBudget = (budget) => numIt(budget?.variantiCalcolate?.totale);

/** Totale commessa = importo contrattuale + varianti accettate. */
export const totaleCommessa = (budget) => importoCommessa(budget) + variantiDaBudget(budget);

/** Ricavi totali della commessa (contratto + varianti + altri ricavi registrati). */
export const ricaviCommessa = (budget, movimenti) => totaleCommessa(budget) + ricaviDaMovimenti(movimenti);

export const STATI_ODA_CONTEGGIATI = ["Inviato","Confermato","Parzialmente ricevuto","Ricevuto","Fattura verificata","Chiuso"];
// ── Costo di un ODA per la commessa: SEMPRE IVA ESCLUSA (ott 2026) ──────────────
// Tutti i costi di commessa sono IVA esclusa (i materiali inseriti a mano lo sono già, e
// l'IVA per un'impresa è detraibile): prima gli ODA entravano con il totale IVA compresa e
// gonfiavano i costi del 22%. Ordine di priorità:
//   1. imponibile della fattura del fornitore (il costo effettivo, verificato);
//   2. fattura registrata prima di ottobre 2026 (solo totale): scorporo con la stessa
//      proporzione imponibile/totale dell'ordine;
//   3. imponibile dell'ordine (salvato, o ricalcolato dalle righe per gli ordini vecchi).
const numero = (v) => numIt(v);
const numRiga = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;
function imponibileRighe(righe) {
  return (righe || []).reduce((s, r) => s + numRiga(r.quantita) * numRiga(r.prezzoUnitario) * (1 - numRiga(r.sconto) / 100), 0);
}
function totaleRighe(righe) {
  return (righe || []).reduce((s, r) => s + numRiga(r.quantita) * numRiga(r.prezzoUnitario) * (1 - numRiga(r.sconto) / 100) * (1 + numRiga(r.iva) / 100), 0);
}
export function imponibileOda(o) {
  if (!o) return 0;
  const haRighe = (o.righe || []).length > 0;
  const impOrdine = typeof o.imponibileTotale === "number" ? o.imponibileTotale : (haRighe ? imponibileRighe(o.righe) : null);
  const totOrdine = typeof o.importoTotale === "number" && o.importoTotale > 0 ? o.importoTotale : (haRighe ? totaleRighe(o.righe) : 0);
  if (o.fattura && o.fattura.imponibile !== undefined && o.fattura.imponibile !== null && o.fattura.imponibile !== "") return numero(o.fattura.imponibile);
  if (o.fattura && o.fattura.importo != null) {
    const tot = numero(o.fattura.importo);
    if (impOrdine != null && totOrdine > 0) return tot * (impOrdine / totOrdine);
    return tot / 1.22; // nessun dato per lo scorporo: aliquota ordinaria
  }
  if (impOrdine != null) return impOrdine;
  return numero(o.importoTotale) / 1.22;
}
export function sommaCostiOda(ordiniAcquisto, projectId) {
  return (ordiniAcquisto||[])
    .filter(o => o.projectId===projectId || o.commessaId===projectId)
    .filter(o => !o.eliminato && STATI_ODA_CONTEGGIATI.includes(o.stato))
    .reduce((s,o) => s + imponibileOda(o), 0);
}

/**
 * Riepilogo standard per le liste (più commesse): ricavi, costi registrati (movimenti +
 * ordini + costi calcolati salvati dal Controllo Economico), margine.
 */
export function economiaSintetica({ budget, movimenti, ordini, projectId, fontOre, costiCalcolati }) {
  return totaliCommessa({ budget, movimenti, costoOda: sommaCostiOda(ordini || [], projectId), fontOre, costiCalcolati });
}

/**
 * W453 — IL calcolo dei totali di una commessa. Lo usano il Controllo Economico, i report, la
 * Relazione finale, la Cabina di Regia e tutte le viste con più commesse (via economiaSintetica):
 * così lo stesso numero non può più venire diverso in due punti.
 *  • ricavi = contratto + varianti accettate + ricavi registrati come movimenti;
 *  • costi = movimenti (i cartellini solo con fonte Presenze) + ordini di acquisto avviati
 *    (IVA esclusa) + manodopera calcolata (Programmazione/Rapportini) + «Le mie ore» + noleggi.
 * costiCalcolati: quelli calcolati ORA (Controllo Economico, documenti); se mancano, quelli
 * salvati sul budget all'ultima apertura del Controllo Economico (budget.costiCalcolati).
 * totaleVarianti: dal riepilogo delle varianti appena letto; se manca, quello salvato sul budget.
 */
export function totaliCommessa({ budget, movimenti = [], costoOda = 0, fontOre, costiCalcolati, totaleVarianti } = {}) {
  const cc = costiCalcolati || budget?.costiCalcolati || null;
  const contratto = importoCommessa(budget);
  const varianti = totaleVarianti !== undefined && totaleVarianti !== null ? numIt(totaleVarianti) : variantiDaBudget(budget);
  const ricaviMov = ricaviDaMovimenti(movimenti);
  const ricavi = contratto + varianti + ricaviMov;
  // W450: senza fonte delle ore passata vale quella con cui sono stati calcolati i costi
  const fonte = fontOre || cc?.fonteOre;
  const costiMov = costiDaMovimenti(movimenti, fonte);
  const calcolati = costiCalcolatiDaBudget({ costiCalcolati: cc });
  const costi = costiMov + numIt(costoOda) + calcolati;
  const margine = ricavi - costi;
  return { contratto, varianti, ricaviMovimenti: ricaviMov, ricavi, costiMovimenti: costiMov, costoOda: numIt(costoOda), costiCalcolati: calcolati,
    costi, margine, marginePercent: ricavi > 0 ? (margine / ricavi) * 100 : null,
    aggiornatiIl: costiCalcolati ? "ora" : (cc?.aggiornatiIl || null) };
}

/**
 * Per le viste con TUTTE le commesse: mappe projectId → ricavi e projectId → costi
 * registrati (movimenti + ordini di acquisto), con le stesse regole di questo file.
 *
 * W450 (31/10/2026) — stessi numeri del Controllo Economico, sempre:
 *  • fonte delle ore: quella dell'azienda (fontOre); se chi chiama non la passa, quella con
 *    cui il Controllo Economico ha calcolato i costi della commessa (costiCalcolati.fonteOre).
 *    Prima qui i movimenti dei cartellini contavano sempre, anche con fonte Programmazione o
 *    Rapportini, e il margine delle «3 cose di oggi» poteva crollare mentre il Controllo
 *    Economico diceva «Ottimo»;
 *  • se per errore una commessa ha più documenti di budget, vale quello che usa il Controllo
 *    Economico (il primo per identificativo, come lo restituisce Firestore), mai l'ultimo letto.
 */
export function budgetPrincipale(budgets) {
  const conId = (budgets || []).filter(Boolean);
  if (!conId.length) return null;
  // stesso ordine di Firestore (per identificativo, carattere per carattere)
  return conId.slice().sort((a, b) => { const x = String(a.id || ""), y = String(b.id || ""); return x < y ? -1 : x > y ? 1 : 0; })[0];
}
export function mappeEconomicheOrg({ budget = [], movimenti = [], ordini = [], fontOre } = {}) {
  const ricavi = {}, costi = {}, fonte = {}, aggiornati = {};
  const perProgetto = {};
  budget.forEach(b => { if (b?.projectId) (perProgetto[b.projectId] = perProgetto[b.projectId] || []).push(b); });
  Object.entries(perProgetto).forEach(([pid, lista]) => {
    const b = lista.length > 1 ? budgetPrincipale(lista) : lista[0];
    ricavi[pid] = totaleCommessa(b);
    fonte[pid] = fontOre || b?.costiCalcolati?.fonteOre || undefined;
    const cc = costiCalcolatiDaBudget(b);
    if (cc) costi[pid] = (costi[pid] || 0) + cc;
    const quando = dataAggiornamentoCosti(b);
    if (quando) aggiornati[pid] = quando;
  });
  movimenti.forEach(m => {
    const pid = m?.projectId; if (!pid) return;
    if (eRicavo(m)) { if (!m.eliminato) ricavi[pid] = (ricavi[pid] || 0) + numIt(m.importo); }
    else if (contaComeCosto(m, fonte[pid] || fontOre)) costi[pid] = (costi[pid] || 0) + numIt(m.importo);
  });
  ordini.forEach(o => {
    if (!o || o.eliminato || !STATI_ODA_CONTEGGIATI.includes(o.stato)) return;
    const pid = o.commessaId || o.projectId; if (!pid) return;
    costi[pid] = (costi[pid] || 0) + imponibileOda(o);
  });
  return { ricavi, costi, aggiornati };
}

/**
 * W453 — quando sono stati calcolati manodopera e noleggi salvati sul budget (ultima apertura
 * del Controllo Economico). Le viste con tutte le commesse li mostrano accanto al margine:
 * «costi aggiornati al …». null se mai calcolati.
 */
export function dataAggiornamentoCosti(budget) {
  const t = budget?.costiCalcolati?.aggiornatiIl;
  if (!t) return null;
  const d = typeof t.toDate === "function" ? t.toDate() : (t.seconds ? new Date(t.seconds * 1000) : new Date(t));
  return Number.isNaN(d?.getTime?.()) ? null : d;
}

/** «12/10» (o «12/10/2025» se di un altro anno) della data più vecchia tra quelle date; "" se nessuna. */
export function testoAggiornamentoCosti(date = []) {
  const valide = date.filter(d => d instanceof Date && !Number.isNaN(d.getTime()));
  if (!valide.length) return "";
  const d = new Date(Math.min(...valide.map(x => x.getTime())));
  const gg = String(d.getDate()).padStart(2, "0"), mm = String(d.getMonth() + 1).padStart(2, "0");
  return d.getFullYear() === new Date().getFullYear() ? `${gg}/${mm}` : `${gg}/${mm}/${d.getFullYear()}`;
}

/**
 * Data di competenza di un movimento ("YYYY-MM-DD"), per i filtri per periodo. I movimenti
 * nascono in modi diversi: data esplicita, data nei dati del modulo, giorno del cartellino
 * (anno e mese nel riferimento presenze), oppure solo il momento di creazione.
 */
export function dataMovimento(m) {
  const p2 = (n) => String(n).padStart(2, "0");
  if (typeof m?.data === "string" && /^\d{4}-\d{2}-\d{2}/.test(m.data)) return m.data.slice(0, 10);
  if (typeof m?.dati?.data === "string" && /^\d{4}-\d{2}-\d{2}/.test(m.dati.data)) return m.dati.data.slice(0, 10);
  if (m?.presenzeRef && m?.dati?.giorno) {
    const parti = String(m.presenzeRef).split("_"); // orgId_uid_anno_mese
    const anno = parti[parti.length - 2], mese = parti[parti.length - 1];
    if (/^\d{4}$/.test(anno) && /^\d{1,2}$/.test(mese)) return `${anno}-${p2(mese)}-${p2(m.dati.giorno)}`;
  }
  // Movimenti di un cartellino approvato prima di ottobre 2026: il giorno non era salvato nei
  // dati, ma è nell'id del movimento (pres_<presenzeRef>_<giorno>_<commessa>). Senza questo la
  // giornata finiva alla data dell'approvazione.
  if (m?.presenzeRef && typeof m?.id === "string" && m.id.startsWith(`pres_${m.presenzeRef}_`)) {
    const giorno = m.id.slice(`pres_${m.presenzeRef}_`.length).split("_")[0];
    const parti = String(m.presenzeRef).split("_");
    const anno = parti[parti.length - 2], mese = parti[parti.length - 1];
    if (/^\d{1,2}$/.test(giorno) && /^\d{4}$/.test(anno) && /^\d{1,2}$/.test(mese)) return `${anno}-${p2(mese)}-${p2(giorno)}`;
  }
  const ts = m?.creadaAt || m?.createdAt;
  const d = ts?.toDate ? ts.toDate() : null;
  return d ? `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}` : "";
}

/**
 * MARGINE PREVISTO DELLA COMMESSA — unica formula (ottobre 2026), usata dal Controllo
 * Economico e, sommata sulle commesse aperte, da Nexiora Insight.
 *   costo previsto = budget di costo (personale + materiali previsti) + costo materiali
 *                    previsto delle varianti accettate o eseguite (utils/varianti.js);
 *   margine previsto = ricavi accettati − il maggiore tra costi reali e costo previsto
 *                    (se i costi reali hanno già superato il previsto, conta il reale).
 * Senza budget di costo il margine previsto non esiste (null): non si inventa.
 * costoPrevistoVarianti: se non indicato si legge dal riepilogo salvato sul budget.
 */
export function marginePrevistoCommessa({ budget, ricavi, costiReali, costoPrevistoVarianti }) {
  const base = numIt(budget?.costoPersonalePrev) + numIt(budget?.costoMaterialiPrev);
  const varianti = costoPrevistoVarianti !== undefined ? numIt(costoPrevistoVarianti) : numIt(budget?.variantiCalcolate?.costoPrevisto);
  if (!(base > 0)) return { costoPrevistoBase: 0, costoPrevistoVarianti: varianti, costoPrevisto: 0, marginePrevisto: null };
  const costoPrevisto = base + varianti;
  return { costoPrevistoBase: base, costoPrevistoVarianti: varianti, costoPrevisto,
    marginePrevisto: numIt(ricavi) - Math.max(numIt(costiReali), costoPrevisto) };
}

/** Commessa aperta: né chiusa, né archiviata, né cartella cliente (madre), né area personale. */
export const commessaAperta = (p) => !!p && p.statoCommessa !== "chiusa" && !p.archived && !p.isParent && !p.senzaCliente;
